<script setup lang="ts">
import { computed } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useWorksheetComposable } from './useWorksheetComposable';
import { formatDate } from '@/utils/dateUtils';
import { getTextVariant } from '@/utils/stringUtils';
import InputNumber from 'primevue/inputnumber';

interface Props {
  optionData: any;
  permission?: boolean;
  editAdult?: any;
  editChild?: any;
  editNumberOfRoom?: any;
  hotelWorkSheet?: any;
  isCruise?: boolean;
  hotelTitle?: string;
}
const props = defineProps<Props>();

const emits = defineEmits<{
  'update:editAdult': [value: any];
  'update:editChild': [value: any];
  'update:editNumberOfRoom': [value: any];
  handleSaveAdult: [value: any];
  handleSaveChild: [value: any];
  handleSaveNumberOfRoom: [value: any];
}>();

const editAdultData = computed({
  get() {
    return props.editAdult;
  },
  set(value) {
    emits('update:editAdult', value);
  }
});

const editChildData = computed({
  get() {
    return props.editChild;
  },
  set(value) {
    emits('update:editChild', value);
  }
});

const editNumberOfRoomData = computed({
  get() {
    return props.editNumberOfRoom;
  },
  set(value) {
    emits('update:editNumberOfRoom', value);
  }
});

const { hasUserPermission } = useWorksheetComposable();
const currentLanguage = getCurrentLocale();

const handleSaveAdult = (adt: number) => {
  emits('handleSaveAdult', adt);
};

const handleSaveChild = (chd: number) => {
  emits('handleSaveChild', chd);
};

const handleSaveNumberOfRoom = (numOfRooms: number) => {
  emits('handleSaveNumberOfRoom', numOfRooms);
};

const getHotelOption = (option: any) => {
  let result = '';
  option.hotels.forEach((hotel: any, indexHotel: number) => {
    if (indexHotel > 0) {
      result += ',\n';
    }
    result += `${hotel.hotel} (${hotel.hCode})`;
  });
  return result;
};

const getRoomOption = (option: any) => {
  let result = '';
  option.hotels.forEach((hotel: any) => {
    const roomTexts = hotel.rooms.map((room: any) => `${getTextVariant(currentLanguage, room.room, room.roomFr)} (${room.code})`);
    result += roomTexts.join(', ') + '\n';
  });
  return result;
};
</script>
<template>
  <div>
    <div class="option hotel-inform">
      <div class="lead-option">{{ !isCruise ? $t('worksheet.hotel') : hotelTitle }}</div>
      <div class="detail-option flex justify-content-between flex-wrap lg:flex-nowrap">
        <div>
          <div class="destination font-bold mt-2 mb-2">
            <div class="flex gap-3">
              <div class="min-w-label">{{ $t('common.destination') }}:</div>
              <div class="font-normal">{{ optionData.airportCode }} ({{ optionData.dest }})</div>
            </div>
          </div>
          <div class="hotel font-bold mb-2">
            <div class="flex gap-3">
              <div class="min-w-label">{{ $t('worksheet.hotel') }}:</div>
              <div class="font-normal white-space-pre-line">{{ getHotelOption(optionData) }}</div>
            </div>
          </div>
          <div class="hotel-room-cat font-bold mb-2">
            <div class="flex gap-3">
              <div class="min-w-label">{{ $t('worksheet.hotel_room_category') }}:</div>
              <div class="font-normal white-space-pre-line">{{ getRoomOption(optionData) }}</div>
            </div>
          </div>
          <div class="departure-date font-bold mb-2">
            <div class="flex gap-3">
              <div class="min-w-label">{{ $t('worksheet.departure_date') }}:</div>
              <div class="font-normal">{{ formatDate(optionData.depDate, 'dd-LLL-yyyy', false, currentLanguage) }}</div>
            </div>
          </div>
          <div class="departure-date font-bold mb-2" v-if="isCruise">
            <div class="flex gap-3">
              <div class="min-w-label">{{ $t('worksheet.return_date') }}:</div>
              <div class="font-normal">{{ formatDate(optionData.retDate, 'dd-LLL-yyyy', false, currentLanguage) }}</div>
            </div>
          </div>
          <div class="duration font-bold mb-2">
            <div class="flex gap-3">
              <div class="min-w-label">{{ $t('worksheet.duration') }}:</div>
              <div class="font-normal w-6rem">{{ optionData.duration }}</div>
            </div>
          </div>
          <div class="departure-date font-bold mb-2">
            <div class="flex gap-3">
              <div class="min-w-label">{{ $t('advance_quote.form.additional_note') }}:</div>
              <div class="font-normal">{{ optionData.additionalNote }}</div>
            </div>
          </div>
        </div>
        <div class="flex flex-column align-items-end">
          <div class="brochure font-bold mb-2 mt-2" v-if="!isCruise">
            <div class="flex">
              <div class="min-w-label">{{ $t('worksheet.brochure') }}:</div>
              <div class="font-normal w-6rem">{{ optionData.brochure }}</div>
            </div>
          </div>
          <div class="adults font-bold mb-2 mt-2">
            <div class="flex" v-if="!editAdultData.isEdit">
              <span class="text-center mr-4">
                <i v-if="hasUserPermission(hotelWorkSheet, permission)" class="pi pi-pencil cursor-pointer" @click="editAdultData = { isEdit: true, data: optionData.adt }"></i>
              </span>
              <div class="min-w-label">{{ $t('common.adults') }}:</div>
              <div class="font-normal w-6rem">{{ optionData.adt }}</div>
            </div>
            <div class="flex" v-else>
              <div class="min-w-label">{{ $t('common.adults') }}:</div>
              <div class="w-3rem">
                <InputNumber v-model="editAdultData.data" :min="0" />
              </div>
              <div class="text-center ml-2 flex gap-2 align-items-center">
                <i class="pi pi-check cursor-pointer" @click="handleSaveAdult(editAdultData.data)"></i>
                <i class="pi pi-times cursor-pointer" @click="editAdultData = { isEdit: false, data: optionData.adt }"></i>
              </div>
            </div>
          </div>
          <div class="child font-bold mb-2">
            <div class="flex" v-if="!editChildData.isEdit">
              <span class="text-center mr-4">
                <i v-if="hasUserPermission(hotelWorkSheet, permission)" class="pi pi-pencil cursor-pointer" @click="editChildData = { isEdit: true, data: optionData.chd }"></i>
              </span>
              <div class="min-w-label">{{ $t('common.children') }}:</div>
              <div class="font-normal w-6rem">{{ optionData.chd }}</div>
            </div>
            <div class="flex" v-else>
              <div class="min-w-label">{{ $t('common.children') }}:</div>
              <div class="w-3rem">
                <InputNumber v-model="editChildData.data" :min="0" />
              </div>
              <div class="text-center ml-2 flex gap-2 align-items-center">
                <i class="pi pi-check cursor-pointer" @click="handleSaveChild(editChildData.data)"></i>
                <i class="pi pi-times cursor-pointer" @click="editChildData = { isEdit: false, data: optionData.chd }"></i>
              </div>
            </div>
          </div>
          <div class="total font-bold mb-2">
            <div class="flex">
              <div class="min-w-label">{{ $t('worksheet.total_passengers') }}:</div>
              <div class="font-normal w-6rem">{{ optionData.adt + optionData.chd }}</div>
            </div>
          </div>
          <div class="number_room font-bold mb-2">
            <div class="flex" v-if="!editNumberOfRoomData.isEdit">
              <span class="text-center mr-4">
                <i v-if="hasUserPermission(hotelWorkSheet, permission)" class="pi pi-pencil cursor-pointer" @click="editNumberOfRoomData = { isEdit: true, data: optionData.numOfRooms }"></i>
              </span>
              <div class="min-w-label">{{ $t('advance_quote.form.number_of_room') }}:</div>
              <div class="font-normal w-6rem">{{ optionData.numOfRooms }}</div>
            </div>
            <div class="flex" v-else>
              <div class="min-w-label">{{ $t('advance_quote.form.number_of_room') }}:</div>
              <div class="w-3rem">
                <InputNumber v-model="editNumberOfRoomData.data" :min="0" />
              </div>
              <div class="text-center ml-2 flex gap-2 align-items-center">
                <i class="pi pi-check cursor-pointer" @click="handleSaveNumberOfRoom(editNumberOfRoomData.data)"></i>
                <i class="pi pi-times cursor-pointer" @click="editNumberOfRoomData = { isEdit: false, data: optionData.numOfRooms }"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
